import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import './App.css';
import './index.css';

import PreRegister from './pages/PreRegister';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import BuyerDashboard from './pages/BuyerDashboard';
import SupplierDashboard from './pages/SupplierDashboard';
import SupplierProfileEdit from './pages/SupplierProfileEdit';
import SupplierSuccessFees from './pages/SupplierSuccessFees';
import SupplierClarifications from './pages/SupplierClarifications';
import GuidedBidWizard from './pages/GuidedBidWizard';
import PrivacyMyData from './pages/PrivacyMyData';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import DataProtectionPolicy from './pages/DataProtectionPolicy';
import FlatAssistanceWorkflow from './pages/FlatAssistanceWorkflow';
import BuyerDemoJourney from './pages/BuyerDemoJourney';
import SupplierDemoJourney from './pages/SupplierDemoJourney';
import AdminSettings from './pages/AdminSettings';
import AdminDashboard from './pages/AdminDashboard';
import CreateTender from './pages/CreateTender';
import TenderDetails from './pages/TenderDetails';
import NegotiationPage from './pages/NegotiationPage';
import SupplierNegotiationsPage from './pages/SupplierNegotiationsPage';
import ProcurementPolicy from './pages/ProcurementPolicy';
import TemplateLibrary from './pages/TemplateLibrary';
import QuickQuotation from './pages/QuickQuotation';
import VendorDiscovery from './pages/VendorDiscovery';
import ForgotPassword from './pages/ForgotPassword';
import PricingPage from './pages/PricingPage';
import PartnerRegister from './pages/PartnerRegister';
import PartnerDashboard from './pages/PartnerDashboard';
import AdminReports from './pages/AdminReports';
import AdminFYDrill from './pages/AdminFYDrill';
import Demo from './pages/Demo';
import LiveDemoLifecycle from './pages/LiveDemoLifecycle';
import Checkout from './pages/Checkout';
import LandingPage from './pages/LandingPage';
import CommitteeManagement from './pages/CommitteeManagement';
import CommitteeDashboard from './pages/CommitteeDashboard';
import CommitteeTenderView from './pages/CommitteeTenderView';
import ObserverDashboard from './pages/ObserverDashboard';
import ObserverTenderView from './pages/ObserverTenderView';
import ObserverLogin from './pages/ObserverLogin';
import CommitteeSetPassword from './pages/CommitteeSetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import FeedbackWidget from './components/FeedbackWidget';
import DemoModeBanner from './components/DemoModeBanner';
import { PublicBetaBanner, FeedbackWidget as PublicBetaWidget } from './components/PublicBeta';
import SplashScreen from './components/SplashScreen';

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public landing page — Founder-approved F1 redesign (Feb 2026); the ONLY live homepage. */}
      <Route path="/" element={<LandingPage />} />

      {/* Retired homepage variants (Aug 2026 content cleanup) — silently redirect to the one live homepage. */}
      <Route path="/app-home" element={<Navigate to="/" replace />} />
      <Route path="/horizon" element={<Navigate to="/" replace />} />
      <Route path="/portal" element={<Navigate to="/" replace />} />
      
      {/* Public routes */}
      <Route path="/pre-register" element={<PreRegister />} />
      <Route path="/buyer-register" element={<Register />} />
      <Route path="/supplier-register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/vendors" element={<VendorDiscovery />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/partner-register" element={<PartnerRegister />} />
      <Route path="/dsa-register" element={<PartnerRegister />} />
      <Route path="/partner-dashboard" element={<ProtectedRoute><PartnerDashboard /></ProtectedRoute>} />
      <Route path="/admin-reports" element={<ProtectedRoute requiredRole="admin"><AdminReports /></ProtectedRoute>} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/demo/lifecycle/:role" element={<LiveDemoLifecycle />} />
      <Route path="/demo/buyer-journey" element={<BuyerDemoJourney />} />
      <Route path="/demo/supplier-journey" element={<SupplierDemoJourney />} />
      <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
      
      {/* Legacy redirects */}
      <Route path="/register" element={<Register />} />
      
      {/* Buyer routes */}
      <Route path="/buyer-dashboard" element={<ProtectedRoute requiredRole="buyer"><BuyerDashboard /></ProtectedRoute>} />
      <Route path="/buyer-dashboard/tender/new" element={<ProtectedRoute requiredRole="buyer"><CreateTender /></ProtectedRoute>} />
      <Route path="/buyer-dashboard/settings/policy" element={<ProtectedRoute requiredRole="buyer"><ProcurementPolicy /></ProtectedRoute>} />
      <Route path="/buyer-dashboard/templates" element={<ProtectedRoute requiredRole="buyer"><TemplateLibrary /></ProtectedRoute>} />
      <Route path="/buyer-dashboard/quick-quotation" element={<ProtectedRoute requiredRole="buyer"><QuickQuotation /></ProtectedRoute>} />
      <Route path="/buyer-dashboard/committee-management" element={<ProtectedRoute requiredRole="buyer"><CommitteeManagement /></ProtectedRoute>} />
      <Route path="/buyer/dashboard" element={<Navigate to="/buyer-dashboard" replace />} />
      <Route path="/buyer/tender/new" element={<Navigate to="/buyer-dashboard/tender/new" replace />} />
      
      {/* Supplier routes */}
      <Route path="/supplier-dashboard" element={<ProtectedRoute requiredRole="supplier"><SupplierDashboard /></ProtectedRoute>} />
      <Route path="/supplier-dashboard/profile" element={<ProtectedRoute requiredRole="supplier"><SupplierProfileEdit /></ProtectedRoute>} />
      <Route path="/supplier-dashboard/success-fees" element={<ProtectedRoute requiredRole="supplier"><SupplierSuccessFees /></ProtectedRoute>} />
      <Route path="/supplier-dashboard/clarifications" element={<ProtectedRoute requiredRole="supplier"><SupplierClarifications /></ProtectedRoute>} />
      <Route path="/supplier-dashboard/tender/:tenderId/bid" element={<ProtectedRoute requiredRole="supplier"><GuidedBidWizard /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute requiredRole="admin"><AdminSettings /></ProtectedRoute>} />
      <Route path="/privacy" element={<ProtectedRoute><PrivacyMyData /></ProtectedRoute>} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/data-protection-policy" element={<DataProtectionPolicy />} />
      <Route path="/buyer-dashboard/flat-assistance/:qqId" element={<ProtectedRoute requiredRole="buyer"><FlatAssistanceWorkflow /></ProtectedRoute>} />
      <Route path="/supplier-dashboard/flat-assistance/:qqId" element={<ProtectedRoute requiredRole="supplier"><FlatAssistanceWorkflow /></ProtectedRoute>} />
      <Route path="/supplier/dashboard" element={<Navigate to="/supplier-dashboard" replace />} />
      
      {/* Admin routes */}
      <Route path="/admin-dashboard" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/dashboard" element={<Navigate to="/admin-dashboard" replace />} />
      <Route path="/admin/fy-drill" element={<ProtectedRoute requiredRole="admin"><AdminFYDrill /></ProtectedRoute>} />
      
      {/* Shared */}
      <Route path="/tender/:tenderId" element={<ProtectedRoute><TenderDetails /></ProtectedRoute>} />
      <Route path="/buyer/negotiation/:parentType/:parentId" element={<ProtectedRoute><NegotiationPage /></ProtectedRoute>} />
      <Route path="/supplier/negotiations" element={<ProtectedRoute requiredRole="supplier"><SupplierNegotiationsPage /></ProtectedRoute>} />

      {/* Committee & Observer */}
      <Route path="/committee/set-password" element={<CommitteeSetPassword />} />
      <Route path="/observer-login" element={<ObserverLogin />} />
      <Route path="/committee/dashboard" element={<ProtectedRoute requiredRole="committee_member"><CommitteeDashboard /></ProtectedRoute>} />
      <Route path="/committee/tender/:tenderId" element={<ProtectedRoute requiredRole="committee_member"><CommitteeTenderView /></ProtectedRoute>} />
      <Route path="/observer/dashboard" element={<ProtectedRoute requiredRole="committee_observer"><ObserverDashboard /></ProtectedRoute>} />
      <Route path="/observer/tender/:tenderId" element={<ProtectedRoute requiredRole="committee_observer"><ObserverTenderView /></ProtectedRoute>} />
      
      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SplashScreen />
        <PublicBetaBanner />
        <DemoModeBanner />
        <AppRouter />
        <PublicBetaWidget />
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
